import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { MapPin, BadgeCheck, Crown, Navigation, Wallet, Plane, Truck } from "lucide-react";
import StarRating from "@/components/StarRating";
import { useCurrency } from "@/context/CurrencyContext";
import { rateBasisSuffix } from "@/data/rateBasis";
import { shippingETA } from "@/lib/shippingETA";

// Pretty short label for the buyer's country — keeps the badge compact.
const shortCountry = (name) => {
  if (!name) return null;
  const map = {
    "United Kingdom": "UK",
    "United States": "USA",
    "United States of America": "USA",
    "Canada": "Canada",
    "Republic of Ireland": "Ireland",
  };
  return map[name] || name;
};

// Loose equality — handles "UK" vs "United Kingdom", "USA" vs "United States", etc.
const sameCountry = (a, b) => {
  if (!a || !b) return false;
  const norm = (s) => String(s).toLowerCase().trim()
    .replace(/^united kingdom$/, "uk")
    .replace(/^united states( of america)?$/, "usa")
    .replace(/^great britain$/, "uk");
  return norm(a) === norm(b);
};

const ListingCard = ({ listing, distanceLabel }) => {
  const img = listing.images?.[0] || "https://images.unsplash.com/photo-1606744824163-985d376605aa?auto=format&fit=crop&q=80&w=800";
  const { displayCurrency, formatPriceDual, supported, userCountry } = useCurrency();
  const dual = formatPriceDual(listing.price, listing.currency);
  // Always have something to render — fall back to raw price string if FX rates haven't loaded.
  const primaryPrice = dual.primary || `${listing.currency || ""} ${listing.price}`;
  // Buyer's display currency matches the listing currency → no FX needed.
  // Powerful trust signal for diaspora users buying locally.
  const sameCurrency = !!listing.currency && listing.currency.toUpperCase() === displayCurrency;
  const localCcyName = supported[displayCurrency]?.name || displayCurrency;
  const eta = shippingETA(listing, { onlyIfCurated: true });

  // "Ships to your country" — only for products, and only when buyer is in a
  // DIFFERENT country than the listing (the cross-border = diaspora value-prop).
  const showShipsToCountry = listing.type === "product"
    && !!userCountry
    && !!listing.country
    && !sameCountry(userCountry, listing.country);
  const shipsLabel = showShipsToCountry ? shortCountry(userCountry) : null;

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      data-testid={`listing-card-${listing.listing_id}`}
      className="group block bg-white rounded-lg border border-black/10 overflow-hidden transition-all duration-300 hover:shadow-md hover:-translate-y-1"
    >
      <div className="aspect-[4/3] overflow-hidden bg-[#F3EFE9] relative">
        <img
          src={img}
          alt={listing.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <Badge className="absolute top-3 left-3 bg-white/90 text-[#1A1A1A] hover:bg-white capitalize">
          {listing.type}
        </Badge>
        <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
          {listing.owner_is_pro && (
            <Badge className="bg-[#D9923B] text-[#1A2024] border-0 font-semibold">
              <Crown className="w-3 h-3 mr-1"/> PRO
            </Badge>
          )}
          {!listing.owner_is_pro && listing.owner_verified_badge && (
            <Badge className="bg-[#C05A3C] text-white border-0">
              <BadgeCheck className="w-3 h-3 mr-1"/> Verified
            </Badge>
          )}
          {distanceLabel && (
            <Badge className="bg-[#1A2024]/85 text-white border-0 backdrop-blur-sm" data-testid="distance-chip">
              <Navigation className="w-3 h-3 mr-1"/> {distanceLabel}
            </Badge>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display text-lg leading-tight line-clamp-2 group-hover:text-[#C05A3C] transition-colors">
            {listing.title}
          </h3>
          <div className="font-sans-ui font-semibold whitespace-nowrap text-right">
            <div data-testid={`card-price-${listing.listing_id}`}>
              {primaryPrice}
              {listing.type === "service" && rateBasisSuffix(listing.rate_basis, "short") && (
                <span className="text-xs text-muted-foreground"> {rateBasisSuffix(listing.rate_basis, "short")}</span>
              )}
            </div>
            {dual.isConverted && dual.original && (
              <div className="text-[10px] font-normal text-muted-foreground" data-testid={`card-orig-${listing.listing_id}`}>
                listed at {dual.original}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
          <MapPin className="w-3 h-3"/>
          <span>{listing.city ? `${listing.city}, ` : ""}{listing.country}</span>
          <span className="mx-1">·</span>
          <span className="truncate">{listing.category}</span>
        </div>
        {sameCurrency && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#4A5D23] bg-[#4A5D23]/10 rounded-full px-2 py-0.5" data-testid={`card-local-ccy-${listing.listing_id}`}>
            <Wallet className="w-3 h-3"/> Pay in {displayCurrency} — no FX
          </div>
        )}
        {showShipsToCountry && (
          <div className="mt-2 ml-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-[#C05A3C] bg-[#C05A3C]/10 rounded-full px-2 py-0.5" data-testid={`card-ships-to-${listing.listing_id}`}>
            <Plane className="w-3 h-3"/> Ships to {shipsLabel}
          </div>
        )}
        {eta && (
          <div className="mt-2 ml-1 inline-flex items-center gap-1 text-[10px] font-semibold text-[#2A3D36]" data-testid={`card-eta-${listing.listing_id}`}>
            <Truck className="w-3 h-3"/> Ships in {eta.label}
          </div>
        )}
        {listing.owner_rating_count > 0 && (
          <div className="mt-2" data-testid={`card-rating-${listing.listing_id}`}>
            <StarRating value={listing.owner_rating_avg || 0} reviewCount={listing.owner_rating_count} size={12} />
          </div>
        )}
      </div>
    </Link>
  );
};

export default ListingCard;
